import { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const translations = {
  en: {
    // Navbar
    nav: {
      home: 'Home',
      about: 'About',
      services: 'Services',
      contact: 'Contact',
      getQuote: 'Get a Free On-Site Estimate',
    },
    // Hero Section
    hero: {
      badge: '4.9 Rating • 61 Google Reviews',
      title1: 'Professional',
      title2: 'Commercial & Emergency',
      title3: 'Cleaning Services',
      description: 'Transform your space with Gomez Cleaning Service LLC. Serving Los Angeles County and San Fernando Valley with reliable, top-quality cleaning solutions.',
      requestQuote: 'Get a Free On-Site Estimate',
      licensed: 'Licensed & Insured',
      available: 'Available Daily',
      availableDaily: 'Available Daily',
      hours: '7:00 AM - 10:00 PM',
    },
    // About Section
    about: {
      title: 'About',
      titleHighlight: 'Gomez Cleaning Service',
      description: 'Gomez Cleaning Service LLC is a professional cleaning company dedicated to providing exceptional cleaning solutions for homes and businesses throughout Los Angeles County and San Fernando Valley. With our experienced team and commitment to quality, we ensure every space we clean exceeds your expectations.',
      learnMore: 'Learn More About Us',
      pageTitle: 'About',
      pageDescription1: 'Gomez Cleaning Service LLC is a professional cleaning company proudly serving Los Angeles County and the San Fernando Valley. With more than 15 years of experience in the industry, we have built a reputation for delivering exceptional cleaning services to both residential and commercial clients.',
      pageDescription2: 'Our dedicated team of cleaning professionals is committed to transforming your space, whether it\'s your home, office, or construction site. We understand that every client has unique needs, and we tailor our services to meet those specific requirements.',
      missionTitle: 'Our',
      missionHighlight: 'Mission',
      missionText: 'Our mission is to provide top-quality cleaning services that enhance the health, comfort, and appearance of every space we touch. We are dedicated to exceeding our clients\' expectations through professionalism, attention to detail, and a commitment to excellence in everything we do.',
      valuesTitle: 'Our',
      valuesHighlight: 'Values',
      valuesSubtitle: 'The principles that guide our work every day.',
      integrity: 'Integrity',
      integrityDesc: 'We conduct our business with honesty and transparency, building lasting relationships with our clients.',
      excellence: 'Excellence',
      excellenceDesc: 'We strive for excellence in every cleaning job, no matter the size or complexity.',
      reliability: 'Reliability',
      reliabilityDesc: 'Our clients count on us to deliver consistent, high-quality results every single time.',
      customerFocus: 'Customer Focus',
      customerFocusDesc: 'Your satisfaction is our top priority. We listen to your needs and exceed expectations.',
      commitmentTitle: 'Our Commitment to',
      commitmentHighlight: 'Quality',
      commitmentText: 'At Gomez Cleaning Service, quality is not just a goal—it\'s our standard. We ensure every job meets our rigorous quality standards and your complete satisfaction.',
      commitments: [
        'Trained and experienced cleaning professionals',
        'Eco-friendly cleaning products available',
        'Customized cleaning plans for your needs',
        'Background-checked and insured staff',
        'Satisfaction guarantee on all services',
        'Flexible scheduling including weekends'
      ],
      serviceAreas: 'Service Areas',
      serviceAreasText: 'We proudly serve the following areas:',
      losAngeles: 'Los Angeles County',
      sanFernando: 'San Fernando Valley',
      sylmar: 'Sylmar & Surrounding Areas',
      availableMonSat: 'Available Monday - Saturday',
      emergencyAvailable: 'Emergency cleaning available daily',
      ctaTitle: 'Ready to Experience the',
      ctaHighlight: 'Difference?',
      ctaText: 'Let us show you why so many clients trust Gomez Cleaning Service for their cleaning needs.',
      getFreeQuote: 'Get a Free On-Site Estimate',
    },
    // Services Section
    services: {
      title: 'Our',
      titleHighlight: 'Services',
      subtitle: 'Comprehensive cleaning solutions tailored to your specific needs.',
      commercial: 'Commercial Cleaning',
      commercialDesc: 'Professional cleaning for offices, retail spaces, and commercial buildings.',
      residential: 'Residential Cleaning',
      residentialDesc: 'Keep your home spotless with our thorough house cleaning services.',
      postConstruction: 'Post-Construction',
      postConstructionDesc: 'Complete cleanup after renovations or new construction projects.',
      moveInOut: 'Move In & Out Cleaning',
      moveInOutDesc: 'Thorough cleaning for tenants moving in or out of properties.',
      powerWashing: 'Power Washing',
      powerWashingDesc: 'Restore your exterior surfaces with professional pressure washing.',
      emergency: 'Emergency Cleaning 🚨',
      emergencyDesc: 'Available every day during business hours for urgent cleaning needs.',
      viewAll: 'View All Services',
      pageTitle: 'Our',
      pageHighlight: 'Cleaning Services',
      pageSubtitle: 'Comprehensive cleaning solutions for homes and businesses in Los Angeles County and San Fernando Valley.',
      whatsIncluded: "What's Included",
      whoItsFor: "Who It's For",
      benefits: 'Benefits',
      getQuote: 'Get a Free On-Site Estimate',
      customTitle: 'Need a',
      customHighlight: 'Custom Solution?',
      customText: 'Contact us to discuss your specific cleaning needs and get a customized quote.',
      requestQuote: 'Get a Free On-Site Estimate',
    },
    // Why Choose Us
    whyChoose: {
      title: 'Why Choose',
      titleHighlight: 'Us',
      subtitle: 'Experience the difference with Gomez Cleaning Service.',
      licensed: 'Licensed & Insured',
      licensedDesc: 'Fully licensed and insured for your peace of mind.',
      professional: 'Professional & Reliable',
      professionalDesc: 'Experienced team committed to excellence in every job.',
      attention: 'Attention to Detail',
      attentionDesc: "We don't cut corners. Every surface gets the attention it deserves.",
      flexible: 'Flexible Scheduling',
      flexibleDesc: 'We work around your schedule for maximum convenience.',
      satisfaction: 'Satisfaction Guaranteed',
      satisfactionDesc: "Not happy? We'll make it right. Your satisfaction is our priority.",
    },
    // Testimonials / Reviews
    reviews: {
      title: 'What Our',
      titleHighlight: 'Clients Say',
      rating: '4.9 out of 5',
      reviewCount: '61 Google Reviews',
      googleReviews: 'Google Reviews',
      verifiedReviews: 'Verified Google Reviews',
      poweredBy: 'Reviews from Google',
    },
    // CTA Section
    cta: {
      title: 'Ready for a',
      titleHighlight: 'Spotless Space?',
      description: 'Contact us today for a free estimate. Let us transform your home or business with our professional cleaning services.',
      getEstimate: 'Get a Free On-Site Estimate',
      callNow: 'Call Now',
    },
    // Contact Page
    contact: {
      title: 'Contact',
      titleHighlight: 'Us',
      subtitle: "Ready for a cleaner space? Get in touch with us today for a free estimate. We're here to help!",
      formTitle: 'Request a Free',
      formHighlight: 'On-Site Estimate',
      formDescription: 'We offer free in-person estimates to provide you with an accurate and fair price based on your specific needs.',
      yourName: 'Your Name',
      emailAddress: 'Email Address',
      phoneNumber: 'Phone Number',
      serviceInterested: 'Service Interested In',
      selectService: 'Select a service',
      yourMessage: 'Your Message',
      messagePlaceholder: 'Tell us about your cleaning needs...',
      sendMessage: 'Schedule My Estimate',
      sending: 'Sending...',
      thankYou: 'Thank You!',
      successMessage: "Your request has been sent successfully. We'll contact you within 24 hours to schedule your free on-site estimate.",
      sendAnother: 'Send Another Request',
      getInTouch: 'Get in',
      touchHighlight: 'Touch',
      phone: 'Phone',
      clickToCall: 'Click to call',
      email: 'Email',
      clickToEmail: 'Click to email',
      address: 'Address',
      businessHours: 'Business Hours',
      monSat: 'Monday - Saturday: 7:00 AM - 10:00 PM',
      emergencyDaily: 'Emergency cleaning available daily',
      serviceAreasTitle: 'Service Areas',
      serviceAreasText: 'We proudly serve clients throughout:',
      notSure: "Not sure if we serve your area? Give us a call!",
      findUs: 'Find Us',
    },
    // Footer
    footer: {
      description: 'Professional commercial and residential cleaning services in Los Angeles County and San Fernando Valley.',
      licensedBadge: 'Licensed & Insured',
      ourServices: 'Our Services',
      quickLinks: 'Quick Links',
      contactUs: 'Contact Us',
      home: 'Home',
      about: 'About Us',
      services: 'Services',
      contact: 'Contact',
      privacy: 'Privacy Policy',
      terms: 'Terms & Conditions',
      rights: 'All rights reserved.',
      serving: 'Serving Los Angeles County & San Fernando Valley',
      language: 'Language',
    },
    // Legal Pages
    privacy: {
      title: 'Privacy',
      titleHighlight: 'Policy',
      backToHome: '← Back to Home',
    },
    terms: {
      title: 'Terms &',
      titleHighlight: 'Conditions',
      backToHome: '← Back to Home',
    },
  },
  es: {
    // Navbar
    nav: {
      home: 'Inicio',
      about: 'Nosotros',
      services: 'Servicios',
      contact: 'Contacto',
      getQuote: 'Estimación Gratis en Sitio',
    },
    // Hero Section
    hero: {
      badge: '4.9 Calificación • 61 Reseñas de Google',
      title1: 'Servicios de Limpieza',
      title2: 'Comercial y de Emergencia',
      title3: 'Profesionales',
      description: 'Transforme su espacio con Gomez Cleaning Service LLC. Sirviendo al Condado de Los Ángeles y el Valle de San Fernando con soluciones de limpieza confiables y de alta calidad.',
      requestQuote: 'Estimación Gratis en Sitio',
      licensed: 'Licenciado y Asegurado',
      available: 'Disponible Diariamente',
      availableDaily: 'Disponible Diariamente',
      hours: '7:00 AM - 10:00 PM',
    },
    // About Section
    about: {
      title: 'Acerca de',
      titleHighlight: 'Gomez Cleaning Service',
      description: 'Gomez Cleaning Service LLC es una empresa de limpieza profesional dedicada a proporcionar soluciones de limpieza excepcionales para hogares y negocios en todo el Condado de Los Ángeles y el Valle de San Fernando. Con nuestro equipo experimentado y compromiso con la calidad, aseguramos que cada espacio que limpiamos supere sus expectativas.',
      learnMore: 'Conozca Más Sobre Nosotros',
      pageTitle: 'Acerca de',
      pageDescription1: 'Gomez Cleaning Service LLC es una empresa de limpieza profesional que sirve con orgullo al Condado de Los Ángeles y el Valle de San Fernando. Con más de 15 años de experiencia en la industria, hemos construido una reputación por ofrecer servicios de limpieza excepcionales tanto a clientes residenciales como comerciales.',
      pageDescription2: 'Nuestro dedicado equipo de profesionales de limpieza está comprometido a transformar su espacio, ya sea su hogar, oficina o sitio de construcción. Entendemos que cada cliente tiene necesidades únicas, y adaptamos nuestros servicios para cumplir con esos requisitos específicos.',
      missionTitle: 'Nuestra',
      missionHighlight: 'Misión',
      missionText: 'Nuestra misión es proporcionar servicios de limpieza de alta calidad que mejoren la salud, comodidad y apariencia de cada espacio que tocamos. Estamos dedicados a superar las expectativas de nuestros clientes a través del profesionalismo, atención al detalle y un compromiso con la excelencia en todo lo que hacemos.',
      valuesTitle: 'Nuestros',
      valuesHighlight: 'Valores',
      valuesSubtitle: 'Los principios que guían nuestro trabajo todos los días.',
      integrity: 'Integridad',
      integrityDesc: 'Conducimos nuestro negocio con honestidad y transparencia, construyendo relaciones duraderas con nuestros clientes.',
      excellence: 'Excelencia',
      excellenceDesc: 'Nos esforzamos por la excelencia en cada trabajo de limpieza, sin importar el tamaño o la complejidad.',
      reliability: 'Confiabilidad',
      reliabilityDesc: 'Nuestros clientes cuentan con nosotros para entregar resultados consistentes y de alta calidad cada vez.',
      customerFocus: 'Enfoque al Cliente',
      customerFocusDesc: 'Su satisfacción es nuestra máxima prioridad. Escuchamos sus necesidades y superamos expectativas.',
      commitmentTitle: 'Nuestro Compromiso con la',
      commitmentHighlight: 'Calidad',
      commitmentText: 'En Gomez Cleaning Service, la calidad no es solo una meta, es nuestro estándar. Aseguramos que cada trabajo cumpla con nuestros rigurosos estándares de calidad y su completa satisfacción.',
      commitments: [
        'Profesionales de limpieza capacitados y experimentados',
        'Productos de limpieza ecológicos disponibles',
        'Planes de limpieza personalizados para sus necesidades',
        'Personal con verificación de antecedentes y asegurado',
        'Garantía de satisfacción en todos los servicios',
        'Horarios flexibles incluyendo fines de semana'
      ],
      serviceAreas: 'Áreas de Servicio',
      serviceAreasText: 'Servimos con orgullo las siguientes áreas:',
      losAngeles: 'Condado de Los Ángeles',
      sanFernando: 'Valle de San Fernando',
      sylmar: 'Sylmar y Áreas Cercanas',
      availableMonSat: 'Disponible Lunes - Sábado',
      emergencyAvailable: 'Limpieza de emergencia disponible diariamente',
      ctaTitle: '¿Listo para Experimentar la',
      ctaHighlight: 'Diferencia?',
      ctaText: 'Permítanos mostrarle por qué tantos clientes confían en Gomez Cleaning Service para sus necesidades de limpieza.',
      getFreeQuote: 'Estimación Gratis en Sitio',
    },
    // Services Section
    services: {
      title: 'Nuestros',
      titleHighlight: 'Servicios',
      subtitle: 'Soluciones de limpieza integrales adaptadas a sus necesidades específicas.',
      commercial: 'Limpieza Comercial',
      commercialDesc: 'Limpieza profesional para oficinas, espacios comerciales y edificios.',
      residential: 'Limpieza Residencial',
      residentialDesc: 'Mantenga su hogar impecable con nuestros servicios de limpieza.',
      postConstruction: 'Post-Construcción',
      postConstructionDesc: 'Limpieza completa después de renovaciones o nuevas construcciones.',
      moveInOut: 'Limpieza de Mudanza',
      moveInOutDesc: 'Limpieza completa para inquilinos que entran o salen de propiedades.',
      powerWashing: 'Lavado a Presión',
      powerWashingDesc: 'Restaure sus superficies exteriores con lavado a presión profesional.',
      emergency: 'Limpieza de Emergencia 🚨',
      emergencyDesc: 'Disponible todos los días en horario comercial para necesidades urgentes.',
      viewAll: 'Ver Todos los Servicios',
      pageTitle: 'Nuestros',
      pageHighlight: 'Servicios de Limpieza',
      pageSubtitle: 'Soluciones de limpieza integrales para hogares y negocios en el Condado de Los Ángeles y el Valle de San Fernando.',
      whatsIncluded: 'Qué Incluye',
      whoItsFor: 'Para Quién Es',
      benefits: 'Beneficios',
      getQuote: 'Estimación Gratis en Sitio',
      customTitle: '¿Necesita una',
      customHighlight: 'Solución Personalizada?',
      customText: 'Contáctenos para discutir sus necesidades específicas de limpieza y obtener una cotización personalizada.',
      requestQuote: 'Estimación Gratis en Sitio',
    },
    // Why Choose Us
    whyChoose: {
      title: 'Por Qué',
      titleHighlight: 'Elegirnos',
      subtitle: 'Experimente la diferencia con Gomez Cleaning Service.',
      licensed: 'Licenciado y Asegurado',
      licensedDesc: 'Totalmente licenciado y asegurado para su tranquilidad.',
      professional: 'Profesional y Confiable',
      professionalDesc: 'Equipo experimentado comprometido con la excelencia en cada trabajo.',
      attention: 'Atención al Detalle',
      attentionDesc: 'No tomamos atajos. Cada superficie recibe la atención que merece.',
      flexible: 'Horarios Flexibles',
      flexibleDesc: 'Trabajamos según su horario para mayor comodidad.',
      satisfaction: 'Satisfacción Garantizada',
      satisfactionDesc: '¿No está satisfecho? Lo corregiremos. Su satisfacción es nuestra prioridad.',
    },
    // Testimonials / Reviews
    reviews: {
      title: 'Lo Que Dicen',
      titleHighlight: 'Nuestros Clientes',
      rating: '4.9 de 5',
      reviewCount: '61 Reseñas de Google',
      googleReviews: 'Reseñas de Google',
      verifiedReviews: 'Reseñas Verificadas de Google',
      poweredBy: 'Reseñas de Google',
    },
    // CTA Section
    cta: {
      title: '¿Listo para un',
      titleHighlight: 'Espacio Impecable?',
      description: 'Contáctenos hoy para una estimación gratuita. Permítanos transformar su hogar o negocio con nuestros servicios de limpieza profesional.',
      getEstimate: 'Estimación Gratis en Sitio',
      callNow: 'Llamar Ahora',
    },
    // Contact Page
    contact: {
      title: 'Contáctenos',
      titleHighlight: '',
      subtitle: '¿Listo para un espacio más limpio? Póngase en contacto con nosotros hoy para una estimación gratuita. ¡Estamos aquí para ayudar!',
      formTitle: 'Solicite una Estimación',
      formHighlight: 'Gratis en Sitio',
      formDescription: 'Ofrecemos estimaciones gratuitas en persona para proporcionarle un precio preciso y justo basado en sus necesidades específicas.',
      yourName: 'Su Nombre',
      emailAddress: 'Correo Electrónico',
      phoneNumber: 'Número de Teléfono',
      serviceInterested: 'Servicio de Interés',
      selectService: 'Seleccione un servicio',
      yourMessage: 'Su Mensaje',
      messagePlaceholder: 'Cuéntenos sobre sus necesidades de limpieza...',
      sendMessage: 'Programar Mi Estimación',
      sending: 'Enviando...',
      thankYou: '¡Gracias!',
      successMessage: 'Su solicitud ha sido enviada exitosamente. Nos comunicaremos con usted dentro de 24 horas para programar su estimación gratuita en sitio.',
      sendAnother: 'Enviar Otra Solicitud',
      getInTouch: 'Póngase en',
      touchHighlight: 'Contacto',
      phone: 'Teléfono',
      clickToCall: 'Clic para llamar',
      email: 'Correo',
      clickToEmail: 'Clic para enviar correo',
      address: 'Dirección',
      businessHours: 'Horario de Atención',
      monSat: 'Lunes - Sábado: 7:00 AM - 10:00 PM',
      emergencyDaily: 'Limpieza de emergencia disponible diariamente',
      serviceAreasTitle: 'Áreas de Servicio',
      serviceAreasText: 'Servimos con orgullo a clientes en:',
      notSure: '¿No está seguro si servimos su área? ¡Llámenos!',
      findUs: 'Encuéntrenos',
    },
    // Footer
    footer: {
      description: 'Servicios profesionales de limpieza comercial y residencial en el Condado de Los Ángeles y el Valle de San Fernando.',
      licensedBadge: 'Licenciado y Asegurado',
      ourServices: 'Nuestros Servicios',
      quickLinks: 'Enlaces Rápidos',
      contactUs: 'Contáctenos',
      home: 'Inicio',
      about: 'Nosotros',
      services: 'Servicios',
      contact: 'Contacto',
      privacy: 'Política de Privacidad',
      terms: 'Términos y Condiciones',
      rights: 'Todos los derechos reservados.',
      serving: 'Sirviendo al Condado de Los Ángeles y Valle de San Fernando',
      language: 'Idioma',
    },
    // Legal Pages
    privacy: {
      title: 'Política de',
      titleHighlight: 'Privacidad',
      backToHome: '← Volver al Inicio',
    },
    terms: {
      title: 'Términos y',
      titleHighlight: 'Condiciones',
      backToHome: '← Volver al Inicio',
    },
  },
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('language') || 'en';
    }
    return 'en';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'es' : 'en');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t, translations: translations[language] }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageProvider;
